
import { useState, useEffect } from 'react';
import { testimonials } from '../../../mocks/testimonials';

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-teal-50 to-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Client <span className="text-teal-600">Testimonials</span>
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Hear what our valued clients have to say about their experience with us
          </p>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Carousel Container */}
          <div className="bg-white rounded-lg p-12 shadow-xl min-h-[400px] flex flex-col justify-center">
            <div className="text-center mb-8">
              <div className="flex justify-center items-center mb-6">
                {[...Array(5)].map((_, index) => (
                  <i
                    key={index}
                    className={`ri-star-fill text-2xl ${
                      index < testimonials[currentIndex].rating ? 'text-yellow-400' : 'text-gray-300'
                    }`}
                  ></i>
                ))}
              </div>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed italic max-w-3xl mx-auto">
                "{testimonials[currentIndex].review}"
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full overflow-hidden mb-4">
                <img
                  src={testimonials[currentIndex].avatar}
                  alt={testimonials[currentIndex].name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h4 className="text-xl font-bold text-gray-900">{testimonials[currentIndex].name}</h4>
              <p className="text-base text-gray-600">{testimonials[currentIndex].position}</p>
              <p className="text-sm text-teal-600">{testimonials[currentIndex].company}</p>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all duration-300 cursor-pointer z-10"
            aria-label="Previous testimonial"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all duration-300 cursor-pointer z-10"
            aria-label="Next testimonial"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentIndex ? 'bg-teal-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
